import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import OwLabel from "../../shared/OwLabel/OwLabel";
import Typeahead from "../../shared/Typeahead/Typeahead";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import ICatalog from "../../../models/ICatalog";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import IPreformLabelDTO from "../../../models/DTO/IPreformLabelDTO";
import IOnPreformLabelDTO from "../../../models/DTO/IOnPreformLabelDTO";
import UIService from "../../../services/UIService";
import OwButton from "../../shared/OwButton/OwButton";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";

type Props = {};

export const LabelGenerator = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<string | undefined>(undefined);
    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>();
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ICatalog_Item | undefined>();
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);

    const [skuValue, setSkuValue] = useState("");
    const [descriptionValue, setDescriptionValue] = useState("");
    const [color, setColor] = useState<{ text: string; color: string }>();
    const [mil, setMil] = useState("");
    const [qty, setQty] = useState("");
    const [batch, setBatch] = useState<{ text: string; color: string }>();
    const [dom, setDom] = useState<Moment>();

    const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs[]>([
        { title: "dashboard", url: "/dashboard" },
        { title: "label_generator", url: "/labels/generator" },
    ]);

    const doCatalogItemLookup = async (name: string) => {
        if (selectedCatalog) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                name,
                catalogGuid: selectedCatalog,
                ignoreProductGroups: true,
                limit: 100,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setFoundCatalogItems(data.catalog_items);
    };

    const getCatalogs = async () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                view: "CreateReportSidebar",
                fn: "getCatalogs",
            },
        };

        socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);
    };

    const downloadLabel = () => {
        if (selectedCatalogItem) {
            const payload: IPreformLabelDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                itemGuid: selectedCatalogItem.guid,
            };

            if (dom) {
                payload.dom = dom.format("MM/DD/YYYY");
            }

            if (mil) {
                payload.mil = mil;
            }

            if (qty) {
                payload.qty = {
                    text: qty,
                    color: "#000000",
                };
            }

            if (batch) {
                payload.batch = [
                    {
                        text: batch.text,
                        color: batch.color,
                    },
                ];
            }

            if (color) {
                payload.colorLine = [
                    {
                        text: color.text,
                        color: color.color,
                    },
                ];
            }

            if (skuValue !== selectedCatalogItem.sku) {
                payload.catalogItemName = skuValue;
            }

            if (descriptionValue !== selectedCatalogItem.description) {
                payload.catalogItemDescription = descriptionValue;
            }

            socket.emit(EndpointsEnum.CatalogItemLabelDownload, payload);
        }
    };

    const onDownloadLabel = (data: IOnPreformLabelDTO) => {
        UIService.downloadDataFile(data.pdfData, "ItemLabels.pdf", "application/pdf");
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem },
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        { endpoint: EndpointsEnum.CatalogItemOnLabelDownload, fn: onDownloadLabel },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getCatalogs();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const handleCatalogChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        let foundCatalog: ICatalog | undefined;

        for (const catalog of catalogs) {
            if (catalog.guid === value) {
                foundCatalog = catalog;
            }
        }

        if (selectedCatalog !== foundCatalog) {
            setSelectedCatalog(undefined);
            setSelectedCatalogItem(undefined);
            setClearTypeahead(true);
        }

        setSelectedCatalog(foundCatalog ? foundCatalog.guid : undefined);
    };

    const selectCatalogItem = (item: ICatalog_Item) => {
        setSelectedCatalogItem(item);
        setSkuValue(item.sku);
        setDescriptionValue(item.description || "");
    };

    const clearTypeaheadFn = () => {
        setSelectedCatalogItem(undefined);
        setClearTypeahead(false);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        switch (name) {
            case "color":
                setColor({ text: value, color: "#000000" });
                break;
            case "mil":
                setMil(value);
                break;
            case "qty":
                setQty(value);
                break;
            case "batch":
                setBatch({ text: value, color: "#000000" });
                break;
            case "dom":
                setDom(value);
                break;
            case "sku":
                setSkuValue(value);
                break;
            case "description":
                setDescriptionValue(value);
                break;
        }
    };

    const handleDomChange = (date: Moment) => {
        setDom(date);
    };

    return (
        <div className={"LabelGenerator"}>
            <Breadcrumbs crumbs={breadcrumbs} />
            <div className="row">
                <div className="col-12">
                    <OwTitle title={"label_generator"} />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-4">
                    <div>
                        <div>
                            <div>
                                <OwLabel title={"catalog"} isBold={true} isUnderline={true} showIsReqAsterik={true} />
                            </div>
                            <OwSelectOption
                                name={"catalog"}
                                fn={handleCatalogChange}
                                options={catalogs}
                                selectedOption={selectedCatalog}
                                selectedOptionProp={""}
                                titleProp={"name"}
                                valueProp={"guid"}
                            />
                        </div>
                        {selectedCatalog ? (
                            <div className={"mt-3"}>
                                <div>
                                    <OwLabel title={"catalog_item"} isBold={true} isUnderline={true} showIsReqAsterik={true} />
                                </div>
                                <Typeahead
                                    isSelectableFn={() => true}
                                    typeaheadApiFn={doCatalogItemLookup}
                                    typeaheadItems={foundCatalogItems}
                                    titleProp={"name"}
                                    selectedItemTitleProp={"name"}
                                    selectedItemFn={selectCatalogItem}
                                    selectedItem={selectedCatalogItem}
                                    clearTypeahead={clearTypeahead}
                                    clearTypeaheadCallback={clearTypeaheadFn}
                                    scrollableList={true}
                                    maxResults={100}
                                />
                            </div>
                        ) : null}

                        {selectedCatalogItem ? (
                            <div>
                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"sku"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwFnInput fn={handleChange} name={"sku"} value={skuValue} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"description"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwFnInput fn={handleChange} name={"description"} value={descriptionValue} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"color"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwFnInput fn={handleChange} name={"color"} value={color?.text} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"mil"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwFnInput fn={handleChange} name={"mil"} value={mil} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"qty"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwFnInput fn={handleChange} name={"qty"} value={qty} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"batch"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwFnInput fn={handleChange} name={"batch"} value={batch?.text} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <div>
                                        <OwLabel title={"dom"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div>
                                        <OwDateInput fn={handleDomChange} />
                                    </div>
                                </div>

                                <div className={"mt-3"}>
                                    <OwButton title={"generate"} fn={downloadLabel} />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};
